import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, InteractiveCalendar, InteractiveListCalendar } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { RAW_DATE_FORMAT } from '@/common/constants';
import {
  PayrollCalendarFormFields,
  ToggleOptions,
} from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/payrollCalendar.types';
import { paramsBuilder } from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/PayrollCalendarParams/PayrollCalendarParamsModalForm';
import { PayrollCalendarConfig, RenewalCalendarEditPayload } from '@/modules/Renewals/renewals.types';

import { usePayrollCalendarModalStore, usePayrollCalendarParamsStore } from '../stores';
import { PayrollCalendarModalSteps } from '../stores/useRenewalPayrollCalendarModal.store';

import { usePayrollCalendarDates } from './hooks/usePayrollCalendarDates';
import { PayrollCalendarDatesHeader } from './PayrollCalendarDatesHeader';

const step = 6;

interface Props {
  onCalendarReady?: (payload: RenewalCalendarEditPayload, editableId?: string) => void;
  onClose?: () => void;
}

const PayrollCalendarDates: React.FC<Props> = ({ onCalendarReady, onClose }) => {
  const [toggleOption, setToggleOption] = useState(ToggleOptions.LIST);
  const editMode = usePayrollCalendarModalStore((state) => state.editMode);
  const editableId = usePayrollCalendarModalStore((state) => state.editableId);
  const { dates, onPickDate, onPickDates, page, setPage, initialMonthDate, minDate, maxStep, monthCount } =
    usePayrollCalendarDates(editMode);
  const handleSetStep = usePayrollCalendarModalStore((state) => state.handleSetStep);
  const calendarParams = usePayrollCalendarParamsStore((state) => state.calendarParams);

  const startDate = useMemo(
    () =>
      dayjs(initialMonthDate)
        .add(page * step, 'month')
        .format(RAW_DATE_FORMAT),
    [initialMonthDate, page]
  );

  const monthRange: [string, string] = useMemo(() => {
    const start = dayjs(startDate).format('MMMM');
    const end = dayjs(startDate).add(5, 'month').format('MMMM');
    return [start, end];
  }, [startDate]);

  const maxDate = useMemo(() => {
    const computedMaxDate = dayjs(initialMonthDate).add(monthCount, 'month');
    if (dayjs(dates[dates.length - 1]).isAfter(computedMaxDate)) {
      return dayjs(dates[dates.length - 1]).format(RAW_DATE_FORMAT);
    }
    return computedMaxDate.format(RAW_DATE_FORMAT);
  }, [dates, initialMonthDate, monthCount]);

  const handleSave = useCallback(() => {
    if (!calendarParams || !dates.length) return;

    // paramsBuilder converts form values to raw backend format and returns only
    // the fields relevant to the chosen frequency. Convert to camelCase for the store.
    const built = paramsBuilder(calendarParams);
    const config: PayrollCalendarConfig = {
      startDate: built.start_date,
      frequency: built.frequency,
      ...(built.day_of_week != null ? { dayOfWeek: built.day_of_week } : {}),
      ...(built.first_day_of_month != null ? { firstDayOfMonth: Number(built.first_day_of_month) } : {}),
      ...(built.second_day_of_month != null ? { secondDayOfMonth: Number(built.second_day_of_month) } : {}),
      ...(built.month != null ? { month: built.month } : {}),
      ...(built.day_of_month != null ? { dayOfMonth: Number(built.day_of_month) } : {}),
    };

    const payload: RenewalCalendarEditPayload = {
      name: calendarParams[PayrollCalendarFormFields.CALENDAR_NAME],
      config,
      schedule: dates,
    };

    onCalendarReady?.(payload, editableId);
    onClose?.();
  }, [calendarParams, dates, onCalendarReady, onClose, editableId]);

  return (
    <Box data-testid="PayrollCalendarDates_container">
      <Box round="container1Round" pad="spacing24" background="module">
        <PayrollCalendarDatesHeader
          selectedOption={toggleOption}
          onSelectedOption={setToggleOption}
          onDatesPage={setPage}
          currentMonthPage={page}
          monthRange={monthRange}
          minDate={dayjs(minDate).format(RAW_DATE_FORMAT)}
          maxDate={maxDate}
          onPickDates={onPickDates}
          minPage={0}
          maxPage={maxStep}
        />

        {toggleOption === ToggleOptions.LIST ? (
          <InteractiveListCalendar
            onPickDate={onPickDate}
            dates={dates}
            minDate={dayjs(minDate)}
            testId="PayrollCalendarDates"
          />
        ) : (
          <InteractiveCalendar
            dates={dates}
            onPickDate={onPickDate}
            initialMonthDate={startDate}
            monthCount={step}
            minDate={dayjs(minDate)}
            maxDate={dayjs(maxDate)}
            testId="PayrollCalendarDates"
          />
        )}
      </Box>
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 'spacing24' }}
        align="end"
        justify="end"
        width="medium"
        gap="xs"
      >
        <Box direction="row" gap="xs">
          <AppButton
            testId="PayrollCalendarDates_cancel"
            buttonType="secondary"
            width="control"
            type="button"
            onClick={() => handleSetStep(PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_PARAMS)}
          >
            Back
          </AppButton>
          <AppButton testId="PayrollCalendarDates_submit" type="button" onClick={handleSave} width="control">
            Save (2/2)
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PayrollCalendarDates;
